import React from "react";
import { listAllowedChatAttachmentMimes } from "@intelligems/ig-utils";
import { UserInput as UserInputType } from "@intelligems/ig-types";
import { useExecutionPanelContext } from "@src/features/ai/components/WorkflowEditor/execution/panel/ExecutionPanelContext";
import { SurveyCard } from "@src/features/ai/components/WorkflowEditor/execution/panel/SurveyCard";
import { useIsTextRevealing } from "@src/features/ai/components/WorkflowEditor/execution/turn/AssistantMessage/TextRevealContext";
import { PromptComposer } from "./PromptComposer";

/**
 * Adapts `ExecutionPanelContext` to `PromptComposer`'s plain-prop interface —
 * the composer used by every execution-panel-based AI chat. Consumers that
 * don't have (or want) an `ExecutionPanelContext` — e.g.
 * `AskAtlasBuildExperience`, which owns its own local turn state instead of a
 * workflow-backed `aiChat` — should render `PromptComposer` directly instead
 * of this wrapper.
 *
 * Appearance comes from `slotProps.userInput`, which is the composer's own
 * slot props, read off
 * the context here so nothing has to be drilled through `ExecutionPanel`'s
 * props. Absent, every chat renders exactly as before.
 */
export const UserInput = () => {
  const revealing = useIsTextRevealing();
  const {
    aiChat,
    getSlot,
    getSlotProps,
    onSubmit,
    streaming,
    disabled,
    onSend,
    userInputMessage,
    setUserInputMessage,
    fileUpload,
    placeholder,
    inputRequirement,
  } = useExecutionPanelContext();

  const handleChange = (value: string) =>
    setUserInputMessage({ type: "text", content: value });

  // While a card-shaped question is parked the composer is the escape hatch:
  // typing answers the question in the user's own words instead of picking an
  // option, so the placeholder says so.
  const cardAskParked = ["choice", "confirmation", "survey", "file"].includes(
    inputRequirement.userInput.type
  );
  const submitDisabled = cardAskParked && revealing;

  // While a file ask's card is on screen it owns the picked files — chips with
  // their own remove buttons — so the composer's tile strip would render the
  // same list a second time.
  const cardOwnsAttachments =
    inputRequirement.userInput.type === "file" && !revealing;

  return (
    <PromptComposer
      value={getTextValue(userInputMessage)}
      onChange={handleChange}
      onSubmit={onSubmit}
      disabled={disabled}
      submitDisabled={submitDisabled}
      streaming={streaming}
      onStop={onSend.stop}
      error={aiChat.error?.message}
      accept={
        fileUpload ? listAllowedChatAttachmentMimes().join(",") : undefined
      }
      maxFiles={fileUpload?.maxPerTurn}
      promptInputPreview={
        cardOwnsAttachments ? null : getSlot("promptInputPreview")
      }
      promptInputTools={getSlot("promptInputTools")}
      composerAsk={getSlot("composerAsk")}
      suggestions={getSlot(
        "suggestions",
        <>
          {getSlot("composerSuggestions")}
          <SurveyCard />
        </>
      )}
      placeholder={cardAskParked ? "Or type your own answer..." : placeholder}
      submit={getSlot("submit")}
      slotProps={getSlotProps("userInput")}
    />
  );
};

const getTextValue = (input: UserInputType | null) => {
  if (!input) return "";
  if (input.type === "text") return input.content;
  if (input.type === "choice") return input.label ?? input.choice;
  if (input.type === "multi-choice")
    return input.choices.map((option) => option.label).join(", ");
  if (input.type === "confirmation") return input.confirmed ? "Yes" : "No";
  // A structured answers reply submits via override input and never sits in the draft.
  return "";
};
